import type { Installation } from "@sitescene/types";

import { useOrgSelection } from "@/context/use-org-selection";
import { useInstallations } from "@/features/installations/use-installations";

/**
 * Lookout installations of the selected organization.
 *
 * Read-only for now: creating installations still lives in the internal
 * testing tool (`AddInstallationModal`) and moves here next.
 */
export const LookoutInstallations = () => {
	const { selected } = useOrgSelection();
	const { data, error, isLoading } = useInstallations();

	return (
		<section>
			<h2 className="text-primary text-base font-semibold">
				Lookout Installations
			</h2>
			{selected && (
				<p className="text-secondary mt-1 text-sm">{selected.name}</p>
			)}

			<div className="mt-4">
				<InstallationList
					error={error}
					installations={data ?? []}
					isLoading={isLoading}
				/>
			</div>
		</section>
	);
};

interface InstallationListProps {
	error: Error | null;
	installations: Installation[];
	isLoading: boolean;
}

const InstallationList = ({
	error,
	installations,
	isLoading,
}: InstallationListProps) => {
	if (isLoading) {
		return <p className="text-secondary text-sm">Loading installations…</p>;
	}

	if (error) {
		return (
			<p className="text-error-primary text-sm">
				Could not load installations. {error.message}
			</p>
		);
	}

	if (installations.length === 0) {
		return (
			<p className="text-secondary text-sm">
				This organization has no installations yet.
			</p>
		);
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full text-left text-sm">
				<thead className="text-tertiary border-secondary border-b">
					<tr>
						<th className="py-2 pr-4 font-medium" scope="col">
							Installation ID
						</th>
						<th className="py-2 pr-4 font-medium" scope="col">
							Location
						</th>
						<th className="py-2 font-medium" scope="col">
							Created
						</th>
					</tr>
				</thead>
				<tbody>
					{installations.map((installation) => (
						<tr
							className="border-secondary text-primary border-b"
							key={installation.id}
						>
							<td className="py-2 pr-4 font-mono">
								{installation.external.id}
							</td>
							<td className="py-2 pr-4">
								{formatLocation(installation.location)}
							</td>
							<td className="py-2">{formatDate(installation.createdAt)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

/**
 * `location` is a ref: the API returns the raw id unless populated, in which
 * case the location's name is the useful thing to show.
 */
const formatLocation = (location: Installation["location"]): string => {
	return typeof location === "string" ? location : location.name;
};

const formatDate = (value: Date | string): string => {
	return new Date(value).toLocaleDateString();
};
